"use client";

import React, { useState } from "react";
import Image, { ImageProps } from "next/image";

const ERROR_IMG_SRC =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==";

export function ImageWithFallback({
  src,
  alt,
  width,
  height,
  fill,
  className,
  style,
  onError,
  sizes,
  priority,
  loading,
  ...rest
}: Omit<ImageProps, "onError"> & { onError?: () => void }) {
  const [didError, setDidError] = useState(false);

  const handleError = () => {
    setDidError(true);
    if (onError) onError();
  };

  const isFill = fill || (!width && !height);

  if (didError || !src) {
    if (isFill) {
      return (
        <div
          className={`absolute inset-0 bg-gray-100 flex items-center justify-center ${className ?? ""}`}
          style={style}
        >
          <img src={ERROR_IMG_SRC} alt="Error loading image" className="object-contain w-12 h-12 opacity-30" />
        </div>
      );
    }

    return (
      <div
        className={`inline-block bg-gray-100 text-center align-middle ${className ?? ""}`}
        style={{ width, height, ...style }}
      >
        <div className="flex items-center justify-center w-full h-full">
          <img src={ERROR_IMG_SRC} alt="Error loading image" className="object-contain w-12 h-12 opacity-30" />
        </div>
      </div>
    );
  }

  const isPriority = !!priority;

  if (width || height || fill) {
    return (
      <Image
        src={src}
        alt={alt || "Image"}
        className={className}
        style={style}
        width={width}
        height={height}
        fill={fill}
        sizes={sizes}
        onError={handleError}
        priority={isPriority}
        loading={isPriority ? "eager" : loading}
        fetchPriority={isPriority ? "high" : undefined}
        {...rest}
      />
    );
  }

  return (
    <div className={`relative ${className ?? ""}`} style={style}>
      <Image
        src={src}
        alt={alt || "Image"}
        className="object-cover"
        fill
        sizes={sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 426px"}
        onError={handleError}
        priority={isPriority}
        loading={isPriority ? "eager" : loading}
        fetchPriority={isPriority ? "high" : undefined}
        {...rest}
      />
    </div>
  );
}

